// src/services/graphService.js
// All Microsoft Graph API calls for SharePoint & M365

export const getAccessToken = async (msalInstance) => {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error("No active account");
  const { loginRequest } = await import("../authConfig");
  const response = await msalInstance.acquireTokenSilent({
    ...loginRequest,
    account,
  });
  return response.accessToken;
};

const graphFetch = async (msalInstance, url, options = {}) => {
  const token = await getAccessToken(msalInstance);
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (res.status === 202 || res.status === 204) return null;
  const text = await res.text();
  // An empty body on a FAILED response (e.g. a 429/5xx with no payload) must
  // still throw — returning null here let callers treat a failed request as
  // "succeeded with no data", which crashes confusingly downstream instead of
  // surfacing the real error.
  if (!text) {
    if (!res.ok) throw new Error(`Graph error ${res.status}`);
    return null;
  }
  let data;
  try {
    data = JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Graph error ${res.status}: could not parse response (${parseError.message})`);
  }
  if (!res.ok)
    throw new Error(data?.error?.message || `Graph error ${res.status}`);
  return data;
};

export const createGraphService = (msalInstance) => {
  const call = (url, opts) => graphFetch(msalInstance, url, opts);
  const spBase = () =>
    `https://graph.microsoft.com/v1.0/sites/${process.env.REACT_APP_SHAREPOINT_SITE_ID}`;
  const siteIdCache = new Map();

  const resolveSiteUrl = async (siteUrl) => {
    if (siteIdCache.has(siteUrl)) return siteIdCache.get(siteUrl);
    const url = new URL(siteUrl);
    const hostname = url.hostname;
    const siteName = url.pathname.split("/").pop();
    const graphUrl = `https://graph.microsoft.com/v1.0/sites/${hostname}:/sites/${siteName}`;
    const data = await call(graphUrl);
    siteIdCache.set(siteUrl, data.id);
    return data.id;
  };

  return {
    // USER
    getCurrentUser: () =>
      call(
        "https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,jobTitle,department,userPrincipalName",
      ),
    searchUsers: (q) =>
      call(
        `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${encodeURIComponent(q)}') or startswith(mail,'${encodeURIComponent(q)}')&$select=id,displayName,mail,jobTitle&$top=20`,
      ),
    getAllUsers: () =>
      call(
        `https://graph.microsoft.com/v1.0/users?$select=id,displayName,mail,jobTitle,department,accountEnabled&$top=999&$filter=accountEnabled eq true`,
      ),

    // EXTERNAL SITES
    getListItemsFromExternalSite: async (siteUrl, listName, filter = "") => {
      const siteId = await resolveSiteUrl(siteUrl);
      let url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listName}/items?$expand=fields&$top=500`;
      if (filter) url += `&$filter=${filter}`;
      return call(url);
    },

    // LISTS
    createList: (listName, description = "") =>
      call(`${spBase()}/lists`, {
        method: "POST",
        body: JSON.stringify({
          displayName: listName,
          description: description,
          columns: [
            { name: "Title", text: {} },
          ],
          list: { template: "genericList" },
        }),
      }),

    getListItems: (listName, filter = "") => {
      let url = `${spBase()}/lists/${listName}/items?$expand=fields&$top=500`;
      if (filter) url += `&$filter=${filter}`;
      return call(url);
    },
    getListItem: (listName, id) =>
      call(`${spBase()}/lists/${listName}/items/${id}?$expand=fields`),
    createListItem: (listName, fields) =>
      call(`${spBase()}/lists/${listName}/items`, {
        method: "POST",
        body: JSON.stringify({ fields }),
      }),
    updateListItem: (listName, id, fields) =>
      call(`${spBase()}/lists/${listName}/items/${id}/fields`, {
        method: "PATCH",
        body: JSON.stringify(fields),
      }),
    deleteListItem: (listName, id) =>
      call(`${spBase()}/lists/${listName}/items/${id}`, { method: "DELETE" }),

    // DRIVES / FILES
    getDrives: () => call(`${spBase()}/drives`),
    getFolderContents: (driveId, path = "") => {
      const url = path
        ? `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${path}:/children`
        : `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children`;
      return call(url);
    },
    createFolder: (driveId, parentPath, name) => {
      const url = parentPath
        ? `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${parentPath}:/children`
        : `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children`;
      return call(url, {
        method: "POST",
        body: JSON.stringify({
          name,
          folder: {},
          "@microsoft.graph.conflictBehavior": "fail",
        }),
      });
    },

    // Download file as ArrayBuffer (for template manipulation)
    downloadFile: async (driveId, filePath) => {
      const token = await getAccessToken(msalInstance);
      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${filePath}:/content`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok)
        throw new Error(`Download failed: ${res.status} - ${filePath}`);
      return res.arrayBuffer();
    },

    // Download an existing drive item as PDF using Graph's built-in conversion
    downloadFileAsPdf: async (driveId, itemId) => {
      const token = await getAccessToken(msalInstance);
      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}/content?format=pdf`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok)
        throw new Error(`PDF conversion failed: ${res.status}`);
      return res.arrayBuffer();
    },

    uploadFile: async (driveId, folderPath, fileName, fileContent, contentType) => {
      const token = await getAccessToken(msalInstance);

      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${folderPath}/${fileName}:/content`;

      const mime = contentType ||
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

      const body =
        fileContent instanceof Blob
          ? fileContent
          : new Blob([fileContent], { type: mime });

      const res = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": mime,
        },
        body,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload failed: ${res.status} ${txt}`);
      }

      return res.json();
    },
    deleteFile: (driveId, itemId) =>
      call(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`,
        { method: "DELETE" },
      ),

    renameFile: (driveId, itemId, newName) =>
      call(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ name: newName }),
        },
      ),

    getFileVersions: (driveId, itemId) =>
      call(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}/versions`,
      ),

    // Upload session — handles files of any size (no 4MB limit)
    // Creates a resumable upload session then sends the file in one chunk
    uploadFileSession: async (driveId, folderPath, fileName, fileContent) => {
      const token = await getAccessToken(msalInstance);
      const sessionUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${folderPath}/${fileName}:/createUploadSession`;

      // Step 1: create session
      const sessionRes = await fetch(sessionUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          item: { "@microsoft.graph.conflictBehavior": "replace" },
        }),
      });
      if (!sessionRes.ok)
        throw new Error(`Upload session creation failed: ${sessionRes.status}`);
      const { uploadUrl } = await sessionRes.json();

      // Step 2: upload entire file to the session URL
      // Wrap in Blob — browser fetch forbids setting Content-Length manually,
      // but derives it correctly from a Blob, preventing SharePoint truncation.
      const bytes =
        fileContent instanceof ArrayBuffer
          ? fileContent
          : await fileContent.arrayBuffer();
      const total = bytes.byteLength;
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Range": `bytes 0-${total - 1}/${total}`,
        },
        body: blob,
      });
      if (!uploadRes.ok)
        throw new Error(`Upload session PUT failed: ${uploadRes.status}`);
      return uploadRes.json();
    },
    getFileMetadata: (driveId, path) =>
      call(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${path}?$select=id,name,webUrl,size,lastModifiedDateTime,createdBy`,
      ),
    shareItem: (driveId, itemId, emails) =>
      call(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}/invite`,
        {
          method: "POST",
          body: JSON.stringify({
            recipients: emails.map((e) => ({ email: e })),
            roles: ["write"],
            requireSignIn: true,
            sendInvitation: true,
          }),
        },
      ),

    // DRIVE - Get site default drive
    getSiteDrive: () =>
      call(`${spBase()}/drive?$select=id,name,webUrl`),

    // EMAIL
    sendEmail: (to, subject, htmlBody, cc = []) =>
      call("https://graph.microsoft.com/v1.0/me/sendMail", {
        method: "POST",
        body: JSON.stringify({
          message: {
            subject,
            body: { contentType: "HTML", content: htmlBody },
            toRecipients: (Array.isArray(to) ? to : [to]).map((e) => ({
              emailAddress: { address: e },
            })),
            ccRecipients: cc.map((e) => ({ emailAddress: { address: e } })),
          },
          saveToSentItems: true,
        }),
      }),
  };
};
